// src/firebase/auth.js
// Authentication Service

import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { setPendingTutorAction } from './tutorLinks';
import { useState, useEffect } from 'react';

/**
 * Sign in with email and password
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Get user profile from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      throw new Error('User profile not found. Please contact administrator.');
    }
    
    const userData = userDoc.data();

    // Block login if email verification was required but not yet completed
    if (userData.emailVerificationRequired && !user.emailVerified) {
      await signOut(auth);
      const verifyError = new Error('Please verify your email address before logging in. Check your inbox for a verification link.');
      verifyError.code = 'auth/email-not-verified';
      throw verifyError;
    }

    return {
      uid: user.uid,
      email: user.email,
      ...userData
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Sign out current user
 */
export const logout = async () => {
  try {
    await signOut(auth);
    // Force reload to clear all state
    window.location.href = '/';
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
};

/**
 * Change the signed-in user's own password. Requires their current
 * password to re-authenticate first - Firebase's updatePassword rejects a
 * session that isn't "recent" (auth/requires-recent-login), which a normal
 * signed-in session usually isn't by the time someone goes looking for a
 * change-password screen, so this can't be skipped.
 */
export const changePassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user logged in');
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error('Password change error:', error);
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error('Current password is incorrect.');
    }
    if (error.code === 'auth/weak-password') {
      throw new Error('New password must be at least 6 characters long.');
    }
    if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many attempts. Please wait a while and try again.');
    }
    throw error;
  }
};

/**
 * Get current user profile
 */
export const getCurrentUserProfile = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      return null;
    }
    
    return {
      uid: user.uid,
      email: user.email,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Get user profile error:', error);
    return null;
  }
};

/**
 * Cross-app SSO receive side — call once on app load, before subscribing
 * to auth state. If another app (e.g. ae-course-hosting) redirected here
 * with a custom token in the URL hash, sign in with it and scrub the hash
 * from the URL/history immediately. No-op if there's no inbound token.
 *
 * Returns the inbound `viewSubmission` id, if the redirect carried one
 * (see ae-course-hosting's crossApp.js goToHomeworkSolutions), or null —
 * App.js hands this to StudentDashboard so it can jump straight to that
 * submission's marked PDF once signed in, instead of just landing on the
 * dashboard.
 */
export const consumeInboundSSOToken = async () => {
  const hash = window.location.hash;
  const tokenMatch = hash.match(/ssoToken=([^&]+)/);
  if (!tokenMatch) return null;
  const viewMatch = hash.match(/viewSubmission=([^&]+)/);
  const viewSubmissionId = viewMatch ? decodeURIComponent(viewMatch[1]) : null;
  // Linked tutors: which tutor's dashboard to land on (sent by
  // ae-course-hosting from a specific course) - applied once signed in, see
  // runPendingTutorAction in tutorLinks.js. Ignored if absent or not linked.
  const tutorMatch = hash.match(/tutor=([^&]+)/);
  if (tutorMatch) setPendingTutorAction({ type: 'switch', orgId: decodeURIComponent(tutorMatch[1]) });

  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  try {
    await signInWithCustomToken(auth, decodeURIComponent(tokenMatch[1]));
  } catch (error) {
    console.error('Inbound SSO sign-in error:', error);
    return null;
  }
  return viewSubmissionId;
};

/**
 * Subscribe to authentication state changes
 * Returns unsubscribe function
 */
export const subscribeToAuthState = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      const profile = await getCurrentUserProfile();
      // Block unverified users — sign them out and treat as logged out
      if (profile && profile.emailVerificationRequired && !user.emailVerified) {
        await signOut(auth);
        callback(null);
        return;
      }
      callback(profile);
    } else {
      callback(null);
    }
  });
};

/**
 * React hook for authentication state
 * Usage: const { user, loading, logout } = useAuth();
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToAuthState((profile) => {
      setUser(profile);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return { 
    user, 
    loading, 
    logout: handleLogout 
  };
};

/**
 * Check if current user is a marker
 */
export const isMarker = async () => {
  const profile = await getCurrentUserProfile();
  return profile && profile.role === 'marker';
};

/**
 * Check if current user is a student
 */
export const isStudent = async () => {
  const profile = await getCurrentUserProfile();
  return profile && profile.role === 'student';
};

/**
 * Resend the email verification link.
 * Signs in temporarily with the supplied credentials, sends the email, then signs out.
 */
export const resendVerificationEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(userCredential.user);
    await signOut(auth);
  } catch (error) {
    console.error('Resend verification error:', error);
    throw error;
  }
};